import { Card, IconButton, Dropdown, Filter, Ascending, Descending, ButtonType, IconSize, OverlayMode } from '@soteria/design-system-ve';

import * as styles from './AuditSessionsFilters.css';
import type { AuditSessionSort, AuditSessionPageSize } from './types';
import { SORT_FIELD_OPTIONS, AUDIT_SESSION_PAGE_SIZE_OPTIONS } from './types';

interface SessionsFilterBarProps {
  sort: AuditSessionSort;
  onSortChange: (sort: AuditSessionSort) => void;
  pageSize: AuditSessionPageSize;
  onPageSizeChange: (pageSize: AuditSessionPageSize) => void;
  isFilterExpanded: boolean;
  onFilterToggle: () => void;
  filtersActive: boolean;
  activeFilterCount: number;
  isLoading?: boolean;
}

/**
 * Main filter bar component with sort controls and filter toggle
 * Contains sort field dropdown, sort direction toggle, and filter expansion button
 */
export function SessionsFilterBar({
  sort,
  onSortChange,
  pageSize,
  onPageSizeChange,
  isFilterExpanded,
  onFilterToggle,
  filtersActive,
  activeFilterCount,
  isLoading = false,
}: SessionsFilterBarProps) {
  // Handle sort field change
  const handleSortFieldChange = (selectedItem: string) => {
    const newSort: AuditSessionSort = {
      field: selectedItem as AuditSessionSort['field'],
      direction: sort.direction,
    };

    onSortChange(newSort);
  };

  // Handle sort direction toggle
  const handleSortDirectionToggle = () => {
    const newSort: AuditSessionSort = {
      field: sort.field,
      direction: sort.direction === 'asc' ? 'desc' : 'asc',
    };

    onSortChange(newSort);
  };

  // Handle page size change
  const handlePageSizeChange = (selectedItem: string) => {
    const newPageSize = Number(selectedItem) as AuditSessionPageSize;

    onPageSizeChange(newPageSize);
  };

  return (
    <Card padding="medium">
      <div className={styles.mainFilterBar}>
        <div className={styles.filterBarLeft}>{/* Future: Could add search or other controls here */}</div>

        <div className={styles.controlGroup}>
          {/* Filter Toggle Button */}
          <IconButton
            aria-label={isFilterExpanded ? 'Hide filters' : 'Show filters'}
            buttonType={ButtonType.Secondary}
            className={styles.filterButton}
            data-active={isFilterExpanded}
            data-has-filters={filtersActive}
            disabled={isLoading}
            icon={Filter}
            iconSize={IconSize.Small}
            overlayMode={OverlayMode.Solid}
            title={`${isFilterExpanded ? 'Hide' : 'Show'} filters${filtersActive ? ` (${activeFilterCount} active)` : ''}`}
            onClick={onFilterToggle}
          />

          {/* Sort Direction Toggle */}
          <IconButton
            aria-label={`Sort ${sort.direction === 'asc' ? 'ascending' : 'descending'}`}
            buttonType={ButtonType.Secondary}
            className={styles.sortDirectionButton}
            data-direction={sort.direction}
            disabled={isLoading}
            icon={sort.direction === 'asc' ? Ascending : Descending}
            iconSize={IconSize.Small}
            overlayMode={OverlayMode.Solid}
            title={`Sort ${sort.direction === 'asc' ? 'ascending' : 'descending'}`}
            onClick={handleSortDirectionToggle}
          />

          {/* Sort Field Dropdown */}
          <Dropdown
            className={styles.sortDropdown}
            disabled={isLoading}
            options={SORT_FIELD_OPTIONS}
            placeholder="Sort by..."
            selectedItem={sort.field}
            size="medium"
            onSelectionChange={handleSortFieldChange}
          />

          {/* Page Size Dropdown */}
          <Dropdown
            className={styles.pageSizeDropdown}
            disabled={isLoading}
            options={AUDIT_SESSION_PAGE_SIZE_OPTIONS}
            placeholder="Page size..."
            selectedItem={pageSize.toString()}
            size="medium"
            onSelectionChange={handlePageSizeChange}
          />
        </div>
      </div>
    </Card>
  );
}
